import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import { TableHeader, ProcessingRequestsList, ColumnMenu, OnColumns, ConfigIconCoordinate } from '@/shared/type';
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';
import CommonOnColumnsMenu from '../common/common-on-columns-menu.component.vue';
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    processingRequestsList: ProcessingRequestsList[];
    processingRequestsDisplayTableHeaders: TableHeader[];
    totalCount: number;
    isLoading: boolean;
    processingRequestsDeleteDialog: ColumnMenu;
    visibleColumnMenu: ColumnMenu;
    filterDefs: TableFilterDefs,
    filters: TableFilters,
    pageOptions: DataOptions;
    isFromConfirm: boolean;
};

/**
 * 処理依頼一覧コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter, CommonOnColumnsMenu },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PROCESSING_REQUESTS_LIST;  // テーブル高さ調整用変数
        const router = vueRouter;                                               // 画面遷移用
        const tableHeaderClass: string[] = ['secondary subtitle-2'];            // ヘッダ色
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                   // 1ページあたりの行数
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                       // メッセージダイアログ
        const columnRef = ref<any>(null);                                       // 列設定
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            processingRequestsList: [],                                         // 処理依頼の一覧
            processingRequestsDisplayTableHeaders: [],                          // テーブルのヘッダ情報
            totalCount: 0,                                                      // 処理依頼の総件数
            isLoading: false,                                                   // テーブルがロード中: true ロード中でない: false
            processingRequestsDeleteDialog: {},                                 // 処理依頼の削除ダイアログの表示有無
            visibleColumnMenu: {},                                              // 行メニューの表示有無
            pageOptions: {                                                      // ソート順
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filterDefs: {                                                       // フィルタ定義
                'applicationNumber': { title: '申請番号', types: 'KeyWordPerfectMatch' },
                'applicant': { title: '申請者名', types: 'KeyWordPartialMatch' },
                'organization': { title: '所属組織', types: 'PullDown' },
                'post': { title: '職', types: 'KeyWordPartialMatch' },
                'phoneNumber': { title: '電話番号', types: 'KeyWordPartialMatch' },
                'mail': { title: 'メールアドレス', types: 'KeyWordPartialMatch' },
                'requestDetails': { title: '依頼内容', types: 'PullDown' },
                'applicationAt': { title: '申請日時', types: 'DatePeriod' },
                'approvalStatus': { title: '承認状況', types: 'CheckBox' },
            },
            filters: {},                                                        // フィルタ情報
            isFromConfirm: false                                                // 確認画面からの遷移フラグ
        });

        const pageNation = ref<number>(1);                                      // ページネーション(vuex管理用)
        const itemsPerPage = ref<number>(linesPerPage[1]);                      // 表示件数(vuex管理用)
        const sortBy = ref<string[]>([]);                                       // ソートカラム(vuex管理用)
        const sortDesc = ref<boolean[]>([]);                                    // ソート昇順、降順(vuex管理用)
        const child = ref<any>();                                               // テーブルフィルタ機能
        let isPageLastData = false;                                             // ページの最終データフラグ
        const processingRequestsInitialTableHeaders: TableHeader[] = [          // 処理依頼一覧ヘッダ情報(初期表示ヘッダー)
            // 申請番号
            {
                text: '申請番号',
                value: 'applicationNumber',
                width: '110px',
                class: tableHeaderClass
            },
            // 申請者名
            {
                text: '申請者名',
                value: 'applicant',
                width: '140px',
                class: tableHeaderClass
            },
            // 所属組織
            {
                text: '所属組織',
                value: 'organization',
                width: '200px',
                class: tableHeaderClass
            },
            // 依頼内容
            {
                text: '依頼内容',
                value: 'requestDetails',
                width: '400px',
                class: tableHeaderClass
            },
            // 申請日時
            {
                text: '申請日時',
                value: 'applicationAt',
                width: '170px',
                class: tableHeaderClass
            },
            // 承認状況
            {
                text: '承認状況',
                value: 'approvalStatus',
                width: '130px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        const processingRequestsTableHeaders: TableHeader[] = [                 // 処理依頼一覧ヘッダ情報
            // 申請番号
            {
                text: '申請番号',
                value: 'applicationNumber',
                width: '110px',
                class: tableHeaderClass
            },
            // 申請者名
            {
                text: '申請者名',
                value: 'applicant',
                width: '140px',
                class: tableHeaderClass
            },
            // 所属組織
            {
                text: '所属組織',
                value: 'organization',
                width: '200px',
                class: tableHeaderClass
            },
            // 職
            {
                text: '職',
                value: 'post',
                width: '140px',
                class: tableHeaderClass
            },
            // 電話番号
            {
                text: '電話番号',
                value: 'phoneNumber',
                width: '140px',
                class: tableHeaderClass
            },
            // メールアドレス
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '350px',
                class: tableHeaderClass
            },
            // 依頼内容
            {
                text: '依頼内容',
                value: 'requestDetails',
                width: '400px',
                class: tableHeaderClass
            },
            // 申請日時
            {
                text: '申請日時',
                value: 'applicationAt',
                width: '170px',
                class: tableHeaderClass
            },
            // 承認状況
            {
                text: '承認状況',
                value: 'approvalStatus',
                width: '130px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // 列設定
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            {
                text: '申請番号',
                value: true
            },
            {
                text: '申請者名',
                value: true
            },
            {
                text: '所属組織',
                value: true
            },
            {
                text: '職',
                value: false
            },
            {
                text: '電話番号',
                value: false
            },
            {
                text: 'メールアドレス',
                value: false
            },
            {
                text: '依頼内容',
                value: true
            },
            {
                text: '申請日時',
                value: true
            },
            {
                text: '承認状況',
                value: true
            },
        ];

        /**
         * フィルタ条件のバリデーション
         */
        // 申請番号
        const applicationNumberRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '申請番号')
        ];

        // 申請者名
        const applicantRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '申請者名')
        ];

        // 職
        const postRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '職')
        ];

        // 電話番号
        const phoneNumberRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '電話番号')
        ];

        // メールアドレス
        const mailRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, 'メールアドレス'),
        ];

        // フィルタ用最大桁数
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;

        created();
        /**
         * 初期表示
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // タイトル表示
            ctx.root.$store.commit('setMainTitle', '処理依頼一覧');

            // 処理依頼一覧情報をクリアする
            state.processingRequestsList = [];

            // 初期表示ヘッダ設定
            state.processingRequestsDisplayTableHeaders = processingRequestsInitialTableHeaders;

            // storeからフィルター情報を復元する
            state.filters = ctx.root.$store.state.processingRequests.filters;

            // storeからページ情報を復元する
            pageNation.value = ctx.root.$store.state.processingRequests.pageNation;
            itemsPerPage.value = ctx.root.$store.state.processingRequests.itemsPerPage;

            // storeからソート情報を復元する
            sortBy.value = ctx.root.$store.state.processingRequests.sortBy;
            sortDesc.value = ctx.root.$store.state.processingRequests.sortDesc;

            // storeから確認画面からの遷移フラグを復元する
            state.isFromConfirm = ctx.root.$store.state.processingRequests.isFromConfirm;

            // フィルタ初期設定
            // 初期設定が完了するまでawaitで待つ
            await initFilter();

            // vuexにフィルタ情報が保持されている場合、データを復元する
            const filterIdList = [];
            const filterParameters = [];
            for (const key in state.filters) {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        // 有効なフィルタのIDを保存する
                        filterIdList.push(key);
                        // 有効なフィルタ条件を保存する
                        filterParameters.push(val.condition);
                    }
                }
            }

            // フィルタ情報がある場合
            if (filterIdList.length !== 0) {
                // 子コンポーネントのsetAllFiltersを呼び出し、フィルターを表示する
                // (フィルタ条件更新によるupdateFilterメソッド呼び出し時に、処理依頼一覧情報を取得)
                child.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // フィルタ情報がない場合
                // 処理依頼一覧情報取得処理のみ行う
                updateList();
            }

            // 確認画面からの遷移フラグにfalseを設定する
            state.isFromConfirm = false;
        }

        /**
         * フィルタ初期設定
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // getFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('processingRequests/getFilterConditions');

            // 通信結果の判定を行う
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合、
                // errorDisplayTransitionControlメソッドでエラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // 所属組織
            state.filterDefs['organization'].items = response.data.organizations;
            // 依頼内容
            state.filterDefs['requestDetails'].items = response.data.processRequestCategories;
            // 承認状況
            state.filterDefs['approvalStatus'].chkItems = response.data.approvalStatus;

            // バリデーションを設定する
            // 申請番号
            state.filterDefs['applicationNumber'].validator = applicationNumberRules;
            // 申請者名
            state.filterDefs['applicant'].validator = applicantRules;
            // 職
            state.filterDefs['post'].validator = postRules;
            // 電話番号
            state.filterDefs['phoneNumber'].validator = phoneNumberRules;
            // メールアドレス
            state.filterDefs['mail'].validator = mailRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 確認画面からの遷移以外の場合
            if (!state.isFromConfirm) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 処理依頼一覧を表示更新する
            updateList();
        }

        /**
         * 一覧のページ操作によるイベント
         * @param options ページ情報
         */
        function updateOptions(options: DataOptions) {
            // v-data-tableよりページ数・１ページあたりの行数を受け取り、
            // state内の変数に設定する。
            state.pageOptions = options;

            // データの中身を比較して、いずれも変更されていなかった場合、処理を終了する
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                return;
            }

            // ページ/ソート情報を画面に適用する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 処理依頼一覧を表示更新する
            updateList();
        }

        /**
         * 処理依頼一覧の表示更新
         */
        async function updateList() {

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // ストアにパラメータを格納
            ctx.root.$store.commit('processingRequests/setPageNation', pageNation.value);
            ctx.root.$store.commit('processingRequests/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('processingRequests/setFilters', state.filters);
            ctx.root.$store.commit('processingRequests/setSortBy', sortBy.value);
            ctx.root.$store.commit('processingRequests/setSortDesc', sortDesc.value);

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成            
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 処理依頼一覧取得APIでデータを取得する
            //  GET /api/processing-requests
            //  params: ページ数、１ページあたりの行数、ソートキー、ソート順、フィルタ情報
            const response = await ctx.root.$store.dispatch('processingRequests/getRequests',
                {
                    page: params['page'],                  // ページ数
                    itemsPerPage: params['itemsPerPage'],  // １ページあたりの行数
                    sortKey: params['sortKey'],            // ソートキー
                    sortOrder: params['sortOrder'],        // ソート順
                    filterParams: params['filterParams'],  // フィルタ情報
                });

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 処理依頼一覧のデータをstate.processingRequestsListへ設定する
            state.processingRequestsList = response.data.processingRequests;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.processingRequestsList.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 処理依頼確認画面遷移処理
         * @param processRequestId 処理依頼ID
         */
        function navigationConfirmation(processRequestId: string) {

            // 処理依頼確認画面へ処理依頼IDを渡して遷移する。
            router.push(`/processing-requests/confirmation/${processRequestId}`);
        }

        /**
         * 処理依頼削除処理
         * @param processRequestId 処理依頼ID
         */
        async function deleteRequest(processRequestId: string) {

            // 処理依頼の削除ダイアログを非表示にする
            state.processingRequestsDeleteDialog[processRequestId] = false;

            // 処理依頼削除APIを呼ぶ
            //   DELETE /api/processing-requests/{processRequestId}
            const response = await ctx.root.$store.dispatch('processingRequests/deleteRequest',
                { 'processRequestId': processRequestId });

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 削除完了のダイアログを表示する
            commonRef.value.displayMessageDialog(response);

            // ページの最終データフラグがtrueの場合、ページ情報を-1する
            if (isPageLastData) {
                state.pageOptions.page -= 1;
                pageNation.value -= 1;
            }

            // 処理依頼一覧を表示更新する
            updateList();

        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize() {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 列設定アイコンクリック処理
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを実行する
         */
        function showMenu() {
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };

            // 列設定アイコンの座標を取得する
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            // 列設定メニューコンポーネントのshowMenuを実行する
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に実行する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            // 設定後のテーブルヘッダー情報を取得する
            state.processingRequestsDisplayTableHeaders = displayTableHeaders;
        }

        return {
            tableHeight,
            state,
            pageNation,
            sortBy,
            sortDesc,
            commonRef,
            columnRef,
            processingRequestsInitialTableHeaders,
            processingRequestsTableHeaders,
            linesPerPage,
            itemsPerPage,
            tableLoadingText,
            onColumns,
            child,
            updateOptions,
            updateFilter,
            created,
            onResize,
            navigationConfirmation,
            deleteRequest,
            showMenu,
            getTableHeader
        };
    }
});
